import React from 'react';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

/**
 * Shared component for rendering adjustment cells with styling and tooltips
 * Used by both adj_price and adj_IFMV columns to maintain DRY code
 */
const AdjustmentCell = ({
  rawAdjustments,
  miscAdjustmentTooltips,
  totalAdjustments,
  onAddAdjustment,
  onDeleteAdjustment,
  displayValue,
  rawValue,
}) => {
  const isNegative = totalAdjustments < 0;

  // If no adjustments, render simple value with color
  const finalDisplayValue = totalAdjustments === 0 && miscAdjustmentTooltips.length === 0 
    ? rawValue 
    : displayValue;

  return (
    <Tooltip
      title={
        <div style={{ padding: '8px' }}>
          {/* Current Raw Adjustments */}
          {Object.entries(rawAdjustments)
            .map(([label, value]) => {
              const sign = value >= 0 ? '+' : '';
              return (
                <div key={label}>
                  <span style={{ color: '#000' }}>{`${label}: `}</span>
                  <span style={{ 
                    color: value >= 0 ? '#dc2626' : '#16a34a',
                    fontWeight: 'bold'
                  }}>
                    {`${sign}$${Math.round(value).toLocaleString()}`}
                  </span>
                </div>
              );
            })}

          {/* Divider if we have both types of adjustments */}
          {Object.keys(rawAdjustments).length > 0 && miscAdjustmentTooltips.length > 0 && (
            <div style={{ 
              borderTop: '1px solid #ccc',
              marginTop: '8px',
              paddingTop: '8px',
              marginBottom: '4px',
              fontWeight: 'bold'
            }}>
              Misc Adjustments
            </div>
          )}

          {/* Misc Adjustments */}
          {miscAdjustmentTooltips.map((adjData) => (
            <div key={adjData.key} className="flex items-center justify-between">
              <span>
                <span style={{ color: '#000' }}>{`${adjData.name}: `}</span>
                <span style={{ 
                  color: adjData.adjustment >= 0 ? '#dc2626' : '#16a34a',
                  fontWeight: 'bold'
                }}>
                  {adjData.adjustment >= 0 ? '+' : ''}
                  {typeof adjData.adjustmentValue === 'string' 
                    ? adjData.adjustmentValue.replace(/\.\d+/, '') // Remove decimals from string values
                    : Math.round(adjData.adjustmentValue).toLocaleString()}
                </span>
              </span>
              <IconButton
                onClick={() => onDeleteAdjustment?.(adjData)}
                sx={{ color: 'red' }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </div>
          ))}

          {/* Total Adjustments */}
          <div style={{ 
            borderTop: '1px solid #ccc',
            marginTop: '4px',
            paddingTop: '4px'
          }}>
            <span style={{ color: '#000' }}>Total: </span>
            <span style={{ 
              color: totalAdjustments >= 0 ? '#dc2626' : '#16a34a',
              fontWeight: 'bold'
            }}>
              {totalAdjustments >= 0 ? '+' : ''}
              ${Math.round(totalAdjustments).toLocaleString()}
            </span>
          </div>
        </div>
      }
      placement="top"
      arrow
      componentsProps={{
        popper: {
          sx: {
            '& .MuiTooltip-tooltip': {
              backgroundColor: '#e5e7eb',
              color: 'black',
            },
            '& .MuiTooltip-arrow': {
              color: '#e5e7eb',
            },
          },
        },
      }}
    >
      <div className="flex group justify-end h-full relative">
        <div className="hidden w-6 group-hover:flex relative items-center justify-center">
          <IconButton
            className="mr-0"
            onClick={() => onAddAdjustment?.()}
            sx={{ padding: '1px', fontSize: '0.5rem' }}
          >
            <AddIcon />
          </IconButton>
        </div>
        <Typography className="flex items-center">
          {miscAdjustmentTooltips.length > 0 && <span className="mr-1 text-orange-500">$</span>}
          <span className="font-bold text-black">
            {finalDisplayValue || ''}
          </span>
        </Typography>
      </div>
    </Tooltip>
  );
};

export default AdjustmentCell;
